import {
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
  type VisibilityState,
  type ColumnOrderState,
  type ExpandedState,
  type RowSelectionState,
  type PaginationState,
  type OnChangeFn,
  type Row,
  type Table as TanstackTable,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import * as React from "react";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "./data-table-pagination";
import { useLingui } from "@lingui/react/macro";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  /** Render a detail panel beneath the row when expanded */
  renderSubComponent?: (props: { row: Row<TData> }) => React.ReactElement;
  /** Render a footer row after the last sub-row of an expanded parent */
  renderAfterSubRows?: (parentRow: Row<TData>) => React.ReactNode;
  /** Field in TData that holds uniform sub-rows (tree data) */
  getSubRows?: (row: TData) => TData[] | undefined;
  /** Show pagination controls (default: false) */
  enablePagination?: boolean;
  /** Enable row selection checkboxes (default: false) */
  enableRowSelection?: boolean;
  /** Controlled row selection state (external) */
  rowSelection?: RowSelectionState;
  /** Callback when row selection changes (external) */
  onRowSelectionChange?: (state: RowSelectionState) => void;
  /** Called when a data row is clicked (not the header or empty state) */
  onRowClick?: (row: TData) => void;
  /** Render a toolbar/filter bar above the table — receives the table instance */
  renderToolbar?: (table: TanstackTable<TData>) => React.ReactNode;
  /** Show a message when the table is empty */
  emptyMessage?: string;
  /** Rendered in place of `emptyMessage` when there are no rows (T-905). */
  emptyState?: React.ReactNode;
  /** Controlled column visibility state */
  columnVisibility?: VisibilityState;
  /** Callback when column visibility changes */
  onColumnVisibilityChange?: (state: VisibilityState) => void;
  /** Controlled column order state */
  columnOrder?: ColumnOrderState;
  /** Callback when column order changes */
  onColumnOrderChange?: (state: ColumnOrderState) => void;
  /** className for the wrapper */
  className?: string;
  /** Manual pagination state (server-side) */
  manualPagination?: boolean;
  /** Total page count for manual pagination */
  pageCount?: number;
  /** Controlled pagination state */
  pagination?: PaginationState;
  /** Callback when pagination changes */
  onPaginationChange?: OnChangeFn<PaginationState>;
  /** Manual sorting state (server-side) */
  manualSorting?: boolean;
  /** Controlled sorting state */
  sorting?: SortingState;
  /** Callback when sorting changes */
  onSortingChange?: OnChangeFn<SortingState>;
  /** Total row count for server-side pagination display */
  totalRows?: number;
  /** Map a row to a stable string ID used by row selection state. Defaults to row index. */
  getRowId?: (row: TData, index: number) => string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  renderSubComponent,
  renderAfterSubRows,
  getSubRows,
  enablePagination = false,
  enableRowSelection = false,
  rowSelection: controlledRowSelection,
  onRowSelectionChange: controlledOnRowSelectionChange,
  renderToolbar,
  emptyMessage,
  emptyState,
  className,
  columnVisibility: controlledColumnVisibility,
  onColumnVisibilityChange,
  columnOrder: controlledColumnOrder,
  onColumnOrderChange,
  manualPagination,
  pageCount,
  pagination: controlledPagination,
  onPaginationChange,
  manualSorting,
  sorting: controlledSorting,
  onSortingChange,
  onRowClick,
  totalRows,
  getRowId,
}: DataTableProps<TData, TValue>) {
  const { t } = useLingui();
  const [internalSorting, setInternalSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [internalColumnVisibility, setInternalColumnVisibility] =
    React.useState<VisibilityState>({});

  const sorting = controlledSorting ?? internalSorting;
  const setSorting = (updaterOrValue: SortingState | ((prev: SortingState) => SortingState)) => {
    if (onSortingChange) {
      onSortingChange(updaterOrValue);
    } else {
      setInternalSorting(updaterOrValue);
    }
  };

  const columnVisibility = controlledColumnVisibility ?? internalColumnVisibility;
  const setColumnVisibility = (
    updaterOrValue: VisibilityState | ((prev: VisibilityState) => VisibilityState),
  ) => {
    const next =
      typeof updaterOrValue === "function"
        ? updaterOrValue(columnVisibility)
        : updaterOrValue;
    if (onColumnVisibilityChange) {
      onColumnVisibilityChange(next);
    } else {
      setInternalColumnVisibility(next);
    }
  };
  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const [internalRowSelection, setInternalRowSelection] =
    React.useState<RowSelectionState>({});

  const rowSelection = controlledRowSelection ?? internalRowSelection;
  const handleRowSelectionChange = (
    updaterOrValue:
      | RowSelectionState
      | ((prev: RowSelectionState) => RowSelectionState),
  ) => {
    const next =
      typeof updaterOrValue === "function"
        ? updaterOrValue(rowSelection)
        : updaterOrValue;
    if (controlledOnRowSelectionChange) {
      controlledOnRowSelectionChange(next);
    } else {
      setInternalRowSelection(next);
    }
  };

  const table = useReactTable({
    data,
    columns,
    ...(getRowId ? { getRowId } : {}),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      expanded,
      ...(controlledColumnOrder ? { columnOrder: controlledColumnOrder } : {}),
      ...(enableRowSelection ? { rowSelection } : {}),
      ...(controlledPagination ? { pagination: controlledPagination } : {}),
    },
    ...(onColumnOrderChange
      ? {
          onColumnOrderChange: (updater: ColumnOrderState | ((prev: ColumnOrderState) => ColumnOrderState)) => {
            const next = typeof updater === "function" ? updater(controlledColumnOrder ?? []) : updater;
            onColumnOrderChange(next);
          },
        }
      : {}),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onExpandedChange: setExpanded,
    manualPagination,
    pageCount,
    onPaginationChange,
    manualSorting,
    ...(enableRowSelection
      ? {
          onRowSelectionChange: handleRowSelectionChange,
          enableRowSelection: true,
        }
      : {}),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    ...(enablePagination && !manualPagination
      ? { getPaginationRowModel: getPaginationRowModel() }
      : {}),
    ...(getSubRows ? { getSubRows } : {}),
    ...(renderSubComponent ? { getRowCanExpand: () => true } : {}),
  });

  return (
    <div className={className}>
      {renderToolbar?.(table)}
      <Table>
        <TableHeader className="bg-(--color-surface-muted)">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  colSpan={header.colSpan}
                  className={header.column.columnDef.meta?.className}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row, rowIdx, allRows) => {
              // Detect last sub-row of a group so we can render the footer after it
              const nextRow = allRows[rowIdx + 1];
              const isLastSubRow =
                !!renderAfterSubRows &&
                row.depth > 0 &&
                (rowIdx === allRows.length - 1 || (nextRow != null && nextRow.depth < row.depth));

              let parentRow: Row<TData> | null = null;
              if (isLastSubRow) {
                for (let i = rowIdx - 1; i >= 0; i--) {
                  const candidate = allRows[i];
                  if (candidate != null && candidate.depth < row.depth) {
                    parentRow = candidate;
                    break;
                  }
                }
              }

              return (
                <React.Fragment key={row.id}>
                  <TableRow
                    data-state={row.getIsSelected() ? "selected" : undefined}
                    onClick={
                      onRowClick
                        ? (e) => {
                            if ((e.target as HTMLElement).closest("button, a, [role='menuitem'], [role='option']")) return;
                            onRowClick(row.original);
                          }
                        : enableRowSelection
                          ? () => row.toggleSelected(!row.getIsSelected())
                          : undefined
                    }
                    className={cn(
                      onRowClick || enableRowSelection ? "cursor-pointer" : undefined,
                      row.depth > 0 && "bg-(--color-surface-muted)",
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cell.column.columnDef.meta?.className}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {renderSubComponent && row.getIsExpanded() ? (
                    <TableRow>
                      <TableCell
                        colSpan={row.getVisibleCells().length}
                        className="p-0"
                      >
                        {renderSubComponent({ row })}
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {isLastSubRow && parentRow && renderAfterSubRows && (
                    <TableRow className="bg-(--color-surface-muted) hover:bg-(--color-surface-muted)">
                      <TableCell
                        colSpan={row.getVisibleCells().length}
                        className="h-13 px-3 py-0"
                      >
                        {renderAfterSubRows(parentRow)}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className={
                  emptyState ? "p-3" : "h-24 text-center text-(--color-text-secondary)"
                }
              >
                {emptyState ?? emptyMessage ?? t`No results.`}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {enablePagination && <DataTablePagination table={table} totalRows={totalRows} />}
    </div>
  );
}
